import { FREE_GIFT_CLASSES } from '@/components/blocks/FreeGiftV2/constants';
import { useFreeGiftInteraction } from '@/components/blocks/FreeGiftV2/context/FreeGiftInteractionContext';
import { useFreeGiftPromotion } from '@/components/blocks/FreeGiftV2/context/FreeGiftPromotionContext';
import { ProductWithBubble } from '@/components/blocks/FreeGiftV2/parts/ProductWithBubble';
import { PurchaseBuyItem } from '@/components/blocks/FreeGiftV2/parts/PurchaseBuyItem';
import { PurchaseBlockSx } from '@/components/blocks/FreeGiftV2/styles/freeGift';
import { SkuQuantity } from '@/components/blocks/FreeGiftV2/types';
import DynamicIcon from '@/components/icons/DynamicIcon';
import { useLocalization } from '@/data/Localization';
import { Box, SxProps, Theme, Typography, useTheme } from '@mui/material';
import { useMemo } from 'react';
import {
	MATERIAL_SYMBOL_ICONS,
	OpenSansFontWeights,
} from 'shared/custom/constants/commonConstants';

interface PurchaseBlockProps {
	sx?: SxProps<Theme>;
}

export const PurchaseBlock = ({ sx }: PurchaseBlockProps) => {
	const theme = useTheme();
	const { purchase, purchaseQty, othersQty, currentPartNumber } = useFreeGiftPromotion();
	const { selectedPurchaseSkus, isPromotionComplete, promotionQty } = useFreeGiftInteraction();
	const labels = useLocalization('FreeGift');

	const getDisplayQuantity = (quantity: number) =>
		isPromotionComplete ? quantity : Math.max(1, Math.round(quantity / promotionQty));

	const hasMultipleNoCombinableOptions =
		!Boolean(purchase?.combinable) && (purchase?.purchases?.length ?? 0) > 1;

	const isCurrentPartInPurchase = (purchase?.purchases ?? []).some((group) =>
		group.triggerSKUs.some((sku) => sku.sku === currentPartNumber)
	);

	const noCombinableOptionsQty = hasMultipleNoCombinableOptions
		? (purchase?.purchases ?? []).map((group) => group.quantity)
		: [];
	const hasSameQtyAcrossOptions = noCombinableOptionsQty.every(
		(qty) => qty === noCombinableOptionsQty[0]
	);
	const noCombinableOptionsDisplayQty = hasSameQtyAcrossOptions ? noCombinableOptionsQty[0] : 1;

	const orderedPurchaseSkus = useMemo(() => {
		if (
			hasMultipleNoCombinableOptions &&
			selectedPurchaseSkus.length === 0 &&
			isCurrentPartInPurchase
		) {
			return [{ sku: currentPartNumber, quantity: 1 }];
		}

		return [...selectedPurchaseSkus].sort(
			(a, b) => Number(b.sku === currentPartNumber) - Number(a.sku === currentPartNumber)
		);
	}, [
		selectedPurchaseSkus,
		currentPartNumber,
		hasMultipleNoCombinableOptions,
		isCurrentPartInPurchase,
	]);

	const totalSelectedPurchaseQty = selectedPurchaseSkus.reduce((sum, sku) => sum + sku.quantity, 0);
	const missingPurchaseQty = purchaseQty + othersQty - totalSelectedPurchaseQty;

	return (
		<Box
			component="fieldset"
			sx={[PurchaseBlockSx.fieldset, ...(Array.isArray(sx) ? sx : [sx])]}
			className={FREE_GIFT_CLASSES.fpTrigger}
		>
			<Typography
				component="legend"
				p="3px"
				color="grey.200"
				fontWeight={OpenSansFontWeights.semibold}
				variant="bodySubText"
			>
				{labels.Buy.t()}
			</Typography>
			<Box display="flex" gap="10px" alignItems="center">
				{orderedPurchaseSkus?.slice(0, 3).map((sku: SkuQuantity) => (
					<ProductWithBubble
						key={sku.sku}
						isPurchase
						partNumber={sku.sku}
						quantity={getDisplayQuantity(sku.quantity)}
					/>
				))}
				{missingPurchaseQty > 0 ? (
					<>
						{orderedPurchaseSkus?.length > 0 ? (
							<DynamicIcon
								name={MATERIAL_SYMBOL_ICONS.add}
								color={theme.palette.grey[500]}
								size="30px"
							/>
						) : null}
						<PurchaseBuyItem
							quantity={
								hasMultipleNoCombinableOptions ? noCombinableOptionsDisplayQty : missingPurchaseQty
							}
						/>
					</>
				) : null}
				{orderedPurchaseSkus.length > 3 ? (
					<Typography p="3px" color="grey.500" variant="headingSubtitle">
						+{orderedPurchaseSkus.length - 3}
					</Typography>
				) : null}
			</Box>
		</Box>
	);
};
